import React, { useEffect } from 'react';
import ApiValidationErrorDisplay from '~/components/ApiValidationErrorDisplay';
import { clearApiValidationErrors } from '~/stores/redux/common/apiValidationErrorSlice';
import { schema } from './hero.yup';
import DisplayFormikState from '~/components/DisplayFormikState';
import SubmitResetButtons from '~/components/FormControl/SubmitResetButtons';
import classNames from 'classnames/bind';
const cx = classNames.bind();
import { FormText, FormSelect, FormNumber, Selectize } from '~/components/FormControl';
import { Formik } from 'formik';
import { Card, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import useHeroMetaData from '~/hooks/useHeroMetaData';
import PropTypes from 'prop-types';
import { checkDuplicateName } from '~/utils/validators.yup';
import { API_PREFIX } from './hero.api';
import { Col } from 'react-bootstrap';
import { FileUploadedWithResetCount } from '~/components/FileUploaded';
import { RARITY, ORIGIN } from '~/config/constants';
import { fetchCardMetaDataApi } from '~/pages/CardSaga/cardMetaDataSlice';
import { useNavigate } from 'react-router-dom';

HeroForm.defaultProps = {
    pictureAttributeNames: null,
};
HeroForm.propTypes = {
    initialValues: PropTypes.object.isRequired,
    setResetCount: PropTypes.func.isRequired,
    pictureAttributeNames: PropTypes.array,
    resetCount: PropTypes.number.isRequired,
    formSubmit: PropTypes.func.isRequired,
};

function HeroForm({ initialValues, setResetCount, pictureAttributeNames, resetCount, formSubmit }) {
    const { isLoading } = useSelector((state) => state.loading);
    const { apiValidationErrors } = useSelector((state) => state.apiValidationError);
    const heroMetaData = useHeroMetaData();
    const { ranks, characterClasses, passives, traits, strengths, agilities, vitalities } = heroMetaData;

    const { cardMetaData } = useSelector((state) => state.cardSaga.cardMetaData);
    const { actions, powers, upgrades } = cardMetaData;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (isEmpty(cardMetaData)) {
            dispatch(fetchCardMetaDataApi(navigate));
        }
    }, []);

    return (
        <>
            {/* MUST put `isEmpty(heroMetaData)` in condition, else Bootstrap `Select` will not display value to UI (even though it's already stored value in it)  */}
            {!isEmpty(initialValues) && !isEmpty(heroMetaData) ? (
                <Card>
                    <Card.Body>
                        <Formik
                            initialValues={initialValues}
                            validateOnChange={false}
                            validationSchema={initialValues.id === null ? schema : schema(true)}
                            onSubmit={formSubmit}
                        >
                            {(props) => {
                                // eslint-disable-next-line react/prop-types
                                const { handleSubmit, isSubmitting, resetForm, values } = props;

                                const asyncApiCall = checkDuplicateName(initialValues.name, API_PREFIX); // must put inside formik's callback, so that this `checkDuplicateName` will always return new closure every time formik re-renders, which is necessary as I use <FastField> with `validate` prop, which under the hood compares its current props with last props and only re-render if props actually change. But if I put this snippet outside formik's callback, then `asyncApiCall` of the next re-render is just of the original one from the scope above (this is one of closure features) so it will not re-render even though param passed to `asyncApiCall` (`initialValues.name`) has actually changed.

                                return (
                                    <>
                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />
                                        <Form.Row className="d-flex justify-content-between">
                                            <FormText
                                                sm="3"
                                                name="name"
                                                asyncApiCall={asyncApiCall}
                                                className="d-flex flex-column justify-content-end"
                                            />
                                            {pictureAttributeNames.map((imgName, i) => (
                                                <Col
                                                    key={i}
                                                    sm={3}
                                                >
                                                    <FileUploadedWithResetCount
                                                        pictureName={imgName}
                                                        pictureVal={values[imgName]}
                                                        resetCount={resetCount}
                                                        id={imgName}
                                                    />
                                                </Col>
                                            ))}
                                        </Form.Row>

                                        <Form.Row className="d-flex justify-content-between">
                                            {/* <FormNumber
                                                name={'statPoint'}
                                                sm={3}
                                            />
                                            <FormSelect
                                                name="rankId"
                                                options={ranks || []}
                                                sm={3}
                                                label="Rank"
                                            /> */}
                                            <FormSelect
                                                name="characterClassId"
                                                options={characterClasses || []}
                                                sm={3}
                                                label="Character Class"
                                            />
                                            <FormText
                                                sm="3"
                                                name="season"
                                                className="d-flex flex-column justify-content-end"
                                            />
                                            <FormSelect
                                                name="rarity"
                                                options={RARITY}
                                                sm={3}
                                                label="Rarity"
                                            />
                                        </Form.Row>
                                        <Form.Row className="d-flex justify-content-between">
                                            <Selectize
                                                name="origin"
                                                options={ORIGIN}
                                                sm={3}
                                                label="Origin"
                                            />
                                            <FormSelect
                                                name="power"
                                                options={powers || []}
                                                sm={3}
                                                label="Power"
                                            />
                                            {/* <FormSelect
                                                name="vitalityId"
                                                options={vitalities || []}
                                                sm={3}
                                                label="Vitality level"
                                            /> */}
                                        </Form.Row>
                                        {/* <Form.Row className="d-flex justify-content-between">
                                            <Selectize
                                                name="passiveIds"
                                                options={passives || []}
                                                sm={3}
                                                label="Passive"
                                            />
                                            <Selectize
                                                name="traitIds"
                                                options={traits || []}
                                                sm={3}
                                                label="Trait"
                                            />
                                        </Form.Row> */}
                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />
                                        <ApiValidationErrorDisplay
                                            errors={apiValidationErrors}
                                            clearError={clearApiValidationErrors}
                                        />
                                        <DisplayFormikState props={props} />
                                    </>
                                );
                            }}
                        </Formik>
                    </Card.Body>
                </Card>
            ) : (
                <Card style={{ position: 'relative' }}>
                    <Card.Body className={cx('row', { whirl: isLoading })}></Card.Body>
                </Card>
            )}
        </>
    );
}

export default HeroForm;
